import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import styled from 'styled-components';
import { FiLogOut, FiUser, FiSettings, FiHelpCircle } from 'react-icons/fi';

const UserNavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: auto;
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
  border: 1px solid var(--border-color);
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  text-decoration: none;
  transition: all 0.2s ease;
  gap: 0.75rem;
  
  &:hover {
    background-color: var(--hover-color);
  }
  
  svg {
    color: var(--text-secondary);
    font-size: 1.25rem;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-top: 1px solid var(--border-color);
  color: var(--danger-color);
  cursor: pointer;
  text-align: left;
  gap: 0.75rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--danger-light);
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const UserNav = () => {
  const { user, logout } = useUserAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const navigate = useNavigate();
  const menuRef = React.useRef();

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  if (!user) {
    return null; // Don't render if user is not logged in
  }

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <UserNavContainer>
      <UserMenu ref={menuRef}>
        <UserAvatar onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {getInitials(user.name || user.email)}
        </UserAvatar>
        
        <DropdownMenu isOpen={isMenuOpen}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontWeight: 600 }}>{user.name || user.email}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{user.email}</div>
          </div>
          
          <DropdownItem to="/settings">
            <FiUser />
            <span>Profile</span>
          </DropdownItem>
          
          <DropdownItem to="/settings/account">
            <FiSettings />
            <span>Settings</span>
          </DropdownItem>
          
          <DropdownItem to="/help">
            <FiHelpCircle />
            <span>Help & Support</span>
          </DropdownItem>
          
          <LogoutButton onClick={handleLogout}>
            <FiLogOut />
            <span>Logout</span>
          </LogoutButton>
        </DropdownMenu>
      </UserMenu>
    </UserNavContainer>
  );
};

export default UserNav;
